import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, FileText, Search } from 'lucide-react';

function getMonthRange() {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: first.toISOString().split('T')[0], to: last.toISOString().split('T')[0] };
}

export default function Reports() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [dateFrom, setDateFrom] = useState(getMonthRange().from);
  const [dateTo, setDateTo] = useState(getMonthRange().to);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      const res = await API.get(`/reports/summary?${params}`);
      setSummary(res.data);
    } catch { toast.error('Error cargando informe'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, selectedUser]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      const res = await API.get(`/reports/export/csv?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe_${dateFrom}_${dateTo}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch { toast.error('Error exportando CSV'); }
  };

  const exportPDF = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (selectedUser !== 'all') params.append('user_id', selectedUser);
      const res = await API.get(`/reports/export/pdf?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe_${dateFrom}_${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch { toast.error('Error exportando PDF'); }
  };

  const totalHours = summary.reduce((sum, s) => sum + s.total_hours, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-zinc-50">Informes</h1>
        <p className="text-sm text-zinc-500">Consulta y exporta informes de horas</p>
      </div>

      <Card className="glass-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end flex-wrap">
            <div className="space-y-1 min-w-[160px]">
              <Label className="text-xs text-zinc-500">Usuario</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="bg-zinc-950/50 border-white/10 h-9" data-testid="report-user-select">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10">
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Desde</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="bg-zinc-950/50 border-white/10 h-9 text-sm" aria-label="Fecha desde" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Hasta</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="bg-zinc-950/50 border-white/10 h-9 text-sm" aria-label="Fecha hasta" />
            </div>
            <div className="flex gap-2">
              <Button onClick={exportCSV} variant="secondary" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 h-9" data-testid="export-csv-button">
                <Download className="w-4 h-4 mr-1" /> CSV
              </Button>
              <Button onClick={exportPDF} variant="secondary" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 h-9" data-testid="export-pdf-button">
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base text-zinc-200">Resumen por Usuario</CardTitle>
          <span className="text-lg font-heading font-bold text-amber-500" data-testid="report-total-hours">{totalHours.toFixed(1)}h total</span>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-white/5 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Usuario</TableHead>
                  <TableHead className="text-zinc-400 text-right">Horas Totales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary.length === 0 && (
                  <TableRow><TableCell colSpan={2} className="text-center text-zinc-500 py-8">Sin datos en este periodo</TableCell></TableRow>
                )}
                {summary.map(s => (
                  <TableRow key={s.user_id} className="border-white/5" data-testid={`report-row-${s.user_id}`}>
                    <TableCell className="text-zinc-200 font-medium">{s.user_name}</TableCell>
                    <TableCell className="text-right font-medium text-amber-500/80">{s.total_hours}h</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
